import React, { useState, useEffect, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { toast } from 'react-toastify';
import { useLocation, useNavigate } from 'react-router-dom';
import { Form } from '@unform/web';

import { show } from 'store/modules/dialog/actions';
import api from 'services/api';
import config from 'config/environment';
import AppError from 'utils/AppError';
import { Permissao, StatusRoteiroPromo } from 'utils/variables';
import { downloadFile } from 'utils/index';

import NoPedingData from 'assets/nopendingdata.svg';

import NotFound from 'components/NotFound';
import Card from 'components/Card';
import Button from 'components/Button';
import Widget from 'components/Widget';
import Pagination from 'components/Pagination';
import DropDownMenu, { Action } from 'components/DropDownMenu';
import Input from 'components/Input';
import Avatar from 'components/Avatar';
import {
  FiBookOpen,
  FiCheckCircle,
  FiCoffee,
  FiPlus,
  FiEdit,
  FiSearch,
  FiTrash2,
  FiDownload,
  FiMic,
  FiEye,
} from 'react-icons/fi';

import { Table, Container, WidgetContent } from './styles';
import { Usuario } from 'pages/@types';
import Flag from 'components/Flag';
import { useModal } from 'hooks/modal';
import { usePermit } from 'utils/hooks/usePermit';
import Loader from 'components/ContentLoader/GenericLoader';

interface Workflow {
  emprocesso: number;
  aprovacao: number;
  locucao: number;
  finalizado: number;
}
interface IRoteiro {
  id: number;
  retranca: string;
  status: string;
  duracao: number;
  tipo:
    | 'chamada'
    | 'vinheta'
    | 'promocional'
    | 'chpatrocinada'
    | 'apoio'
    | 'campanha'
    | 'programete'
    | 'afinacao'
    | 'videopatrocinado'
    | 'campanhapatrocinada';
  editor: Usuario;
  comunicador: Usuario;
}

export default function RoteiroPromo() {
  // react-router-dom
  const navigate = useNavigate();
  const location = useLocation();
  /**
   * REDUX
   */
  const dispatch = useDispatch();
  const { callback: callbackDialog } = useSelector(
    (state: any) => state.dialog,
  );
  const hasLocucao = usePermit([], [Permissao.LOCUCAO]);

  /**
   * USE STATE
   */
  const [workflow, setWorkflow] = useState({
    emprocesso: 0,
    aprovacao: 0,
    locucao: 0,
    finalizado: 0,
  } as Workflow);
  const [filtro, setFiltro] = useState('');
  const [roteiros, setRoteiros] = useState<IRoteiro[]>([]);
  const [selected, setSelected] = useState({} as IRoteiro);
  const [page, setPage] = useState(1);
  const [pages, setPages] = useState(1);
  const [loading, setLoading] = useState(false);
  const [loadingDownload, setLoadingDownload] = useState(false);
  const [status, setStatus] = useState(
    location.pathname.includes('/aprovacao') ? 'aprovacao' : 'emprocesso',
  );

  const { showModal, hideModal, updateModal, modalData } = useModal();
  /**
   * USE CALLBACK
   */

  const handleClick = useCallback(s => {
    setStatus(s);
    setPage(1);
    setFiltro('');
  }, []);

  const handleDelete = useCallback(
    r => {
      setSelected(r);
      const optionBase = {
        type: 'question',
        action: 'delete',
        contentHTML: `Remover <b>${r.retranca}</b> ?`,
        confirmButtonText: 'CONFIRMAR',
        callback: {
          url: `/roteiropromo/${r.id}`,
          method: api.delete,
        },
        haveCallback: true,
        successMessage: 'Roteiro removido!',
        cancelButtonText: 'NÃO',
        showCancelButton: false,
        showConfirmButton: true,
        showCloseButton: true,
      };
      dispatch(show(optionBase));
    },
    [dispatch],
  );

  const handleDownload = useCallback(async (r: IRoteiro) => {
    setLoadingDownload(true);
    try {
      await downloadFile({
        url: `${config.API_URL}/roteiropromofile/${r.id}`,
        name: r.retranca,
        fromAPI: true,
      });
    } catch (err) {
      if (err instanceof Error) {
        toast.error(err.message);
      } else {
        const appError = err as AppError;
        console.log(appError);
        toast.error('Falha ao baixar roteiro. Tente novamente mais tarde');
      }
    } finally {
      setLoadingDownload(false);
    }
  }, []);

  const handleSinalizarGravacao = useCallback(
    (r: IRoteiro) => {
      showModal({
        title: 'LOCUÇÃO',
        component: (
          <p>Confirma a gravação da locução do roteiro {r.retranca}?</p>
        ),
        callback: async () => {
          updateModal({ ...modalData, loading: true });
          try {
            await api.patch(`/roteiropromo/${r.id}`, {
              status: StatusRoteiroPromo.FINALIZADO,
            });
            setRoteiros(prevState => prevState.filter(p => p.id !== r.id));
            setWorkflow(prevState => ({
              ...prevState,
              finalizado: prevState.finalizado + 1,
              locucao: prevState.locucao - 1,
            }));
            toast.success(`Roteiro ${r.retranca} finalizado`);
            hideModal();
          } catch (error) {
            if (error instanceof Error) toast.error(error.message);
            else {
              const appError = error as AppError;
              toast.error(appError.message);
            }
          }
        },
      });
    },
    [hideModal, modalData, showModal, updateModal],
  );
  const handleEnviarAprovacao = useCallback(
    (r: IRoteiro) => {
      showModal({
        title: 'ATENÇÃO',
        component: <p>Enviar {r.retranca} para aprovação?</p>,
        callback: async () => {
          updateModal({ ...modalData, loading: true });
          try {
            await api
              .patch(`/roteiropromo/${r.id}`, {
                status: StatusRoteiroPromo.APROVACAO,
              })
              .then(() => {
                setRoteiros(prevState => prevState.filter(p => p.id !== r.id));
                setWorkflow(prevState => ({
                  ...prevState,
                  aprovacao: prevState.aprovacao + 1,
                  emprocesso: prevState.emprocesso - 1,
                }));
                toast.success(`${r.retranca} enviado para aprovação`);
              });
            hideModal();
          } catch (error) {
            if (error instanceof Error) toast.error(error.message);
            else {
              const appError = error as AppError;
              toast.error(appError.message);
            }
          }
        },
      });
    },
    [hideModal, modalData, showModal, updateModal],
  );

  const handleActionSelect = useCallback(
    (r: IRoteiro, action: { value: number }) => {
      switch (action.value) {
        case 1:
          handleDelete(r);
          break;
        case 2:
          handleDownload(r);
          break;
        case 3:
          handleEnviarAprovacao(r);
          break;
        default:
          break;
      }
    },
    [handleDelete, handleDownload, handleEnviarAprovacao],
  );

  /**
   * USE EFFECT
   */

  useEffect(() => {
    const controller = new AbortController();
    async function loadRoteiros() {
      setLoading(true);

      try {
        const response = await api.get('/roteiropromo', {
          signal: controller.signal,
          params: {
            search: filtro.toUpperCase(),
            status,
            page,
          },
        });

        if (response && response.data) {
          setRoteiros(response.data.roteiros);
          setPages(response.data.pages);
          setWorkflow(response.data.workflow);
        } else toast.error('Falha de comunicação com o sistema');
      } catch (e) {
        if (e instanceof Error) toast.error(e.message);
        else if (e instanceof AppError) toast.error(e.message);
        else {
          const appError = e as AppError;
          if (appError.message) toast.error(appError.message);
          else toast.error('Erro inesperado');
        }
      }
      setLoading(false);
    }
    loadRoteiros();
    return () => {
      controller.abort();
    };
  }, [page, status, filtro]);

  // verifica se callback foi realizado com sucesso para então remover item da lista
  useEffect(() => {
    if (callbackDialog && callbackDialog.success) {
      if (callbackDialog.success.action === 'success' && selected) {
        // Atualiza widgets removendo o roteiro deletado
        setWorkflow(prevState => ({
          emprocesso:
            selected.status === StatusRoteiroPromo.EMPROCESSO
              ? prevState.emprocesso - 1
              : prevState.emprocesso,
          aprovacao:
            selected.status === StatusRoteiroPromo.APROVACAO
              ? prevState.aprovacao - 1
              : prevState.aprovacao,
          locucao:
            selected.status === StatusRoteiroPromo.LOCUCAO
              ? prevState.locucao - 1
              : prevState.locucao,
          finalizado:
            selected.status === StatusRoteiroPromo.FINALIZADO
              ? prevState.finalizado - 1
              : prevState.finalizado,
        }));
        // Atualiza tabela removendo o roteiro deletado
        setRoteiros(prevState => prevState.filter(r => r.id !== selected.id));
      }
    }
  }, [callbackDialog, selected]);

  return (
    <Container>
      <WidgetContent>
        <Widget
          title="Em processo"
          type="condensed"
          icon={<FiBookOpen />}
          showLink={false}
          active={status === StatusRoteiroPromo.EMPROCESSO}
          color="info"
          loading={loading}
          handleClick={() => handleClick(StatusRoteiroPromo.EMPROCESSO)}
          data={workflow.emprocesso ? workflow.emprocesso : 0}
        />
        <Widget
          title="Aprovação"
          type="condensed"
          icon={<FiCoffee />}
          active={status === StatusRoteiroPromo.APROVACAO}
          color="warn"
          loading={loading}
          handleClick={() => handleClick(StatusRoteiroPromo.APROVACAO)}
          data={workflow.aprovacao ? workflow.aprovacao : 0}
        />
        <Widget
          title="Locução"
          type="condensed"
          icon={<FiMic />}
          active={status === StatusRoteiroPromo.LOCUCAO}
          color="secondary"
          loading={loading}
          handleClick={() => handleClick(StatusRoteiroPromo.LOCUCAO)}
          data={workflow.locucao ? workflow.locucao : 0}
        />
        <Widget
          title="Finalizado"
          type="condensed"
          icon={<FiCheckCircle />}
          active={status === StatusRoteiroPromo.FINALIZADO}
          color="success"
          loading={loading}
          handleClick={() => handleClick(StatusRoteiroPromo.FINALIZADO)}
          data={workflow.finalizado ? workflow.finalizado : 0}
        />
      </WidgetContent>
      <Card>
        <header>
          <h4>LISTA DE ROTEIROS</h4>
          <Pagination
            total={pages}
            activePage={page}
            onClick={page => setPage(page)}
          />
          <Button
            color="primary"
            href={'/roteiropromo/novo'}
            tooltip="Criar novo Roteiro"
          >
            NOVO ROTEIRO
            <FiPlus />
          </Button>
          <div>
            <Form onSubmit={e => setFiltro(e.searchLocal)}>
              <Input
                name="searchLocal"
                placeholder="Buscar na Tabela"
                icon={<FiSearch />}
              />
            </Form>
          </div>
        </header>
        {loading ? (
          <Loader />
        ) : !roteiros || (roteiros && roteiros.length === 0) ? (
          <div>
            <NotFound img={NoPedingData} title="SEM ROTEIROS PROMO">
              Nenhum roteiro promo encontrado
            </NotFound>
          </div>
        ) : (
          <div style={{ padding: `0 15px` }}>
            <Table>
              <thead>
                <tr>
                  <th>RETRANCA</th>
                  <th>TIPO</th>
                  <th>DUR.</th>
                  <th>EDITOR</th>
                  <th>COMUNICADOR</th>
                  <th>AÇÕES</th>
                </tr>
              </thead>
              <tbody>
                {!!roteiros.length &&
                  roteiros.map(r => (
                    <tr key={r.id}>
                      <td>{r.retranca}</td>
                      <td>
                        <Flag type={r.tipo || 'NA'} name={r.tipo || 'NA'} />
                      </td>
                      <td>{r.duracao} s</td>
                      <td>
                        <Avatar
                          size="40"
                          name={r.editor?.nome}
                          src={r.editor?.avatar_url}
                          round
                        />
                      </td>
                      {r.comunicador ? (
                        <td>
                          <Avatar
                            size="40"
                            name={r.comunicador.nome}
                            src={r.comunicador.avatar_url}
                            round
                          />
                        </td>
                      ) : (
                        <td>...</td>
                      )}
                      <td>
                        <div style={{ display: 'flex' }}>
                          {hasLocucao &&
                            r.status === StatusRoteiroPromo.LOCUCAO && (
                              <Button
                                id={`id_gravado_${r.id}`}
                                fill
                                color="font"
                                round
                                onClick={() => handleSinalizarGravacao(r)}
                              >
                                <FiMic />
                              </Button>
                            )}
                          <Button
                            id={`id_edit_${r.id}`}
                            fill
                            color="font"
                            round
                            href={`/roteiropromo/${r.id}`}
                          >
                            {r.status === StatusRoteiroPromo.LOCUCAO ||
                            r.status === StatusRoteiroPromo.FINALIZADO ? (
                              <FiEye />
                            ) : (
                              <FiEdit />
                            )}
                          </Button>
                          <DropDownMenu
                            id={`acoes_${r.id}`}
                            disabled={false}
                            actions={[
                              {
                                value: 1,
                                label: 'DELETAR',
                                icon: <FiTrash2 />,
                                disabled:
                                  r.status === StatusRoteiroPromo.FINALIZADO,
                              },
                              {
                                value: 2,
                                label: 'DOWNLOAD',
                                icon: <FiDownload />,
                                disabled: loadingDownload,
                              },
                              {
                                value: 3,
                                label: 'ENV APROV.',
                                disabled:
                                  r.status !== StatusRoteiroPromo.EMPROCESSO,
                                icon: <FiCheckCircle />,
                              },
                            ]}
                            handleClick={(action: Action) =>
                              !action.disabled && handleActionSelect(r, action)
                            }
                          />
                        </div>
                      </td>
                    </tr>
                  ))}
              </tbody>
            </Table>
          </div>
        )}
      </Card>
    </Container>
  );
}
